import React from 'react';
import { Link } from 'react-router-dom';
import { Field, AMENITY_LABELS } from '../types';
import { getFieldRatingSummary } from '../data/reviewStore';
import { getDisplayPrice } from '../data/mockData';
import FavoriteButton from './FavoriteButton';

interface FieldCardProps {
  field: Field;
  distanceKm?: number;
  isHighlighted?: boolean;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

const FieldCard: React.FC<FieldCardProps> = ({
  field,
  distanceKm,
  isHighlighted = false,
  onMouseEnter,
  onMouseLeave,
}) => {
  const ratingSummary = getFieldRatingSummary(field.id, field.rating, field.reviewCount);
  const displayPrice = getDisplayPrice(field);
  // Collect available amenities and show first 5
  const availableAmenities = (Object.keys(AMENITY_LABELS) as (keyof typeof AMENITY_LABELS)[])
    .filter((key) => field.amenities[key])
    .slice(0, 5);

  return (
    <Link to={`/saha/${field.id}`} className="group" onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
      <div
        className={`bg-white rounded-xl overflow-hidden border shadow-md transition-all duration-300 ${
          isHighlighted
            ? 'border-primary-500 ring-2 ring-primary-200 shadow-lg'
            : 'border-gray-200 hover:shadow-lg hover:border-primary-500/50'
        }`}
      >
        {/* Image */}
        <div className="relative h-48 overflow-hidden">
          <img
            src={field.images[0]}
            alt={field.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
          />

          {/* Size badge */}
          <div className="absolute top-3 left-3">
            <span className="bg-primary-600 text-white text-xs font-semibold px-2.5 py-1 rounded-full shadow-sm">
              {(field.sizeOptions && field.sizeOptions.length > 0 ? field.sizeOptions.join(', ') : field.size)}
            </span>
          </div>

          {/* Favorite Button */}
          <div className="absolute top-3 right-12">
            <FavoriteButton fieldId={field.id} size="sm" />
          </div>

          {/* Rating badge */}
          <div className="absolute top-3 right-3">
            <span className="bg-white/90 backdrop-blur-sm text-gray-800 text-xs font-semibold px-2.5 py-1 rounded-full flex items-center space-x-1 shadow-sm">
              <svg className="w-3.5 h-3.5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
              <span>{ratingSummary.rating}</span>
            </span>
          </div>
          {field.isReservationPaused && (
            <div className="absolute bottom-3 right-3">
              <span className="bg-rose-600 text-white text-[11px] font-semibold px-2 py-1 rounded-full shadow-sm">
                Rezervasyon Kapalı
              </span>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-4">
          <h3 className="text-gray-900 font-semibold text-lg mb-1 group-hover:text-primary-600 transition-colors">
            {field.name}
          </h3>

          {/* District */}
          <div className="flex items-center justify-between text-gray-500 text-sm mb-3">
            <div className="flex items-center">
              <svg className="w-4 h-4 mr-1 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              {field.district}
            </div>
            {typeof distanceKm === 'number' && (
              <span className="text-xs text-primary-700 font-semibold bg-primary-50 border border-primary-200 rounded-full px-2 py-0.5">
                {distanceKm.toFixed(1)} km
              </span>
            )}
          </div>

          {/* Amenity emoji badges */}
          <div className="flex items-center gap-1.5 mb-3">
            {availableAmenities.map((key) => (
              <span
                key={key}
                title={AMENITY_LABELS[key].label}
                className="bg-gray-100 text-xs px-1.5 py-0.5 rounded-md"
              >
                {AMENITY_LABELS[key].icon}
              </span>
            ))}
            {(Object.keys(AMENITY_LABELS) as (keyof typeof AMENITY_LABELS)[])
              .filter((key) => field.amenities[key]).length > 5 && (
              <span className="text-gray-400 text-xs font-medium">
                +{(Object.keys(AMENITY_LABELS) as (keyof typeof AMENITY_LABELS)[])
                  .filter((key) => field.amenities[key]).length - 5}
              </span>
            )}
          </div>

          {/* Price */}
          <div className="flex items-center justify-between border-t border-gray-100 pt-3">
            <div className="flex items-center text-sm text-gray-400">
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              /saat
            </div>
            <span className="text-primary-600 font-bold text-lg">{displayPrice}₺</span>
          </div>
        </div>
      </div>
    </Link>
  );
};

export default FieldCard;
